import { inject, Injectable } from "@angular/core";
import { Api, categoriesToTreeNodes, logger, RisrFormExpander, RisrFormTransformer, RisrSchemaField, RisrSchemaFieldCategory } from "@fry-it/risr-core";
import { ReportRunRequest, ReportRunResponse, GenerateCSVResponse, AsyncTaskStatus, AsyncTaskResult } from "../models/run_request";
import { isOKResponse } from "@fry-it/risr-core";
import { FilterForm } from "../models/runnable_report";
import { Report } from "../models/report";
import { UserFieldStore } from "@fry-it/risr-features/common";
import { BlueprintStore } from "@fry-it/risr-features/common";
import { RelationStore } from "@fry-it/risr-features/common";

@Injectable({
    providedIn: 'root'
})
export class ReportRunner {
    private readonly api = inject(Api)
    private readonly formExpander = inject(RisrFormExpander)
    private readonly formTransformer = inject(RisrFormTransformer)

    private readonly userFieldStore = inject(UserFieldStore)
    private readonly blueprintStore = inject(BlueprintStore)
    private readonly relationStore = inject(RelationStore)


    async executeById(reportId: string, model: any, options: any, currentUser: string) {
        const endpoint = `/reports/${reportId}/run`;
        return this.executeInternal(endpoint, model, options, currentUser);
    }

    async executeByGroup(groupId: string, model: any, options: any, currentUser: string) {
        const endpoint = `/reports/by_group_id/${groupId}/run`;
        return this.executeInternal(endpoint, model, options, currentUser);
    }

    async execute(report: Report, model: any, options: any, currentUser: string) {
        const endpoint = `/reports/${report.id}/run`;

        const fullModel = this.mergeModel(report, model);
        return this.executeInternal(endpoint, fullModel, options, currentUser);
    }

    private async executeInternal(endpoint: string, model: any, options: any, currentUser: string) {
        const payload: ReportRunRequest = {
            options: options,
            currentUser: currentUser,
            model: model
        }
        const res = await this.api.post<ReportRunResponse>(endpoint, payload);
        if (!isOKResponse(res)) {
            throw res.error;
        }
        return res.data;
    }

    async generateCSV(reportId: string, outputId: string, model: any, options: any, currentUser: string) {
        const endpoint = `/reports/${reportId}/generate_csv`;
        const payload = {
            outputId: outputId,
            options: options,
            currentUser: currentUser,
            model: model
        }
        const res = await this.api.post<GenerateCSVResponse>(endpoint, payload);
        if (!isOKResponse(res)) {
            throw res.error;
        }
        return res.data;
    }

    async getTaskStatus(taskId: string) {
        const res = await this.api.get<AsyncTaskStatus>(`/async-tasks/${taskId}/output`);
        if (!isOKResponse(res)) {
            throw res.error;
        }
        return res.data;
    }

    async getResultUrl(taskId: string) {
        const res = await this.api.get<AsyncTaskResult>(`/async-tasks/${taskId}/download`);
        if (!isOKResponse(res)) {
            throw res.error;
        }
        return res.data;
    }

    async abortTask(taskId: string) {
        const res = await this.api.put(`/async-tasks/abort/${taskId}`);
        if (!isOKResponse(res)) {
            throw res.error;
        }
        return res.data;
    }

    /*
     * Merge model with initial values from report filter form to ensure we always have all required fields
     */
    private mergeModel(report: Report, model: any) {
        const filterForm = report.report.filterForm;
        const initialValues = filterForm.reduce((acc, field) => {
            acc[field.id] = field.initialValue;
            return acc;
        }, {} as any);
        return { ...initialValues, ...model };
    }

    async getForm(filterForm: FilterForm[], model: any) {
        try {
            const expandableFields = await this.filterFormToExpandable(filterForm);
            const expandedFields = await this.formExpander.expandAll(expandableFields);
            const form = await this.formTransformer.fromRisrSchema({
                fields: expandedFields
            }, model);
            return form;
        } catch (error) {
            logger.error('Unexpected error', error instanceof Error ? error : new Error(String(error)));
            throw error;
        }
    }

    async filterFormToExpandable(filterForm: FilterForm[]): Promise<RisrSchemaField[]> {
        const fields: RisrSchemaField[] = [];

        for (const field of filterForm) {
            const fieldFields = await this.getFormForField(field);
            fields.push(...fieldFields);

            // fields.push({
            //     id: field.id,
            //     label: field.label,
            //     expandType: field.field,
            //     required: true,
            //     readonly: false,
            //     extra: field.extra,
            //     options: {

            //     },
            //     initialValue: field.initialValue,
            // });
        }
        return fields;
    }

    async getFormForField(field: FilterForm): Promise<RisrSchemaField[]> {
        const fieldType = field.field;
        const fields: RisrSchemaField[] = [];
        switch (fieldType) {
            case 'eventType':
                const extraEventTypes = field.extra?.allowedValues === 'selected' ? field.extra?.eventTypes : undefined;
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/event-types/search_group_v3',
                            categoriesSourceExtraData: { id: extraEventTypes, purpose: "latestPublished" }
                        }
                    }
                );
                break;

            case 'user':
                // TODO: Do we need to block the stub if all users is not allowed?
                const allowedValues: string[]  = field.extra?.allowedValues as string[] || [];
                const options: RisrSchemaFieldCategory[] = [];
                if (allowedValues.includes('__current__')) {
                    options.push({ name: '__current__', label: $localize`Current user` });
                }
                if (allowedValues.includes('__all__')) {
                    options.push({ name: '__all__', label: $localize`All permissible users` });
                }
                fields.push({
                    name: field.id,
                    type: 'discrete_multiple',
                    label: field.label,
                    required: true,
                    initialValue: field.initialValue,
                    options: {
                        categoriesSource: '/users/stub',
                        extraCategories: options
                    }
                });
                break;

            case 'userField':
                const userFieldId = field.extra?.userField;
                if (!userFieldId) {
                    break;
                }
                const userField = await this.userFieldStore.get(userFieldId);
                const categories = (userField.categories || []).map(userField => ({ name: userField._id, label: userField.name }));
                fields.push({
                        name: 'initialValue',
                        type: 'discrete_multiple',
                        label: $localize`Default value`,
                        initialValue: field.initialValue,
                        required: false,
                        options: {
                            categories: categories
                        }
                    });
                break;

            case 'string':
            case 'number':

                fields.push({
                    name: field.id,
                    type: field.field as 'string' | 'number' | 'date',
                    label: field.label,
                    required: true,
                    initialValue: field.initialValue,
                    options: {
                        hint: $localize`Select a specific date using the calendar`,
                    }
                });
                break;
            case 'date':
                // let initialValue: string;
                // if (typeof field.initialValue == 'string') {
                //     initialValue = parseDate(field.initialValue);
                // } else {
                //     initialValue = '';
                // }
                fields.push({
                    name: field.id,
                    type: 'relative_date',
                    label: field.label,
                    required: true,
                    initialValue: field.initialValue,
                    options: {
                        hint: $localize`Select a specific date using the calendar`,
                    }
                });
                break;

            case 'role':
                const extra = field.extra?.allowedValues === 'selected' ? field.extra?.roles : undefined;
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/roles/stub',
                            categoriesSourceExtraData: { ids: extra }
                        }
                    }
                );
                break;

            case 'blueprint_category':
                const blueprintId = field.extra?.blueprint;
                if (!blueprintId) {
                    break;
                }

                const blueprint = await this.blueprintStore.get(blueprintId);
                fields.push({
                    name: field.id,
                    label: field.label,
                    type: 'tree',
                    required: true,
                    initialValue: field.initialValue,
                    options: {
                        categories: categoriesToTreeNodes(blueprint.categories || [])
                    }
                });
                break;

            case 'relation_category':
                const relationId = field.extra?.relation;
                if (!relationId) {
                    break;
                }

                const relation = await this.relationStore.get(relationId);
                fields.push({
                    name: field.id,
                    label: field.label,
                    type: 'tree',
                    required: true,
                    initialValue: field.initialValue,
                    options: {
                        categories: categoriesToTreeNodes(relation.categories || [])
                    }
                });
                break;

            case 'eventState':
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categories: [
                                { name: 'submitted', label: $localize`Submitted` },
                                { name: 'complete', label: $localize`Complete` },
                            ]
                        }
                    }
                );
                break;

            case 'bookingType':
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/booking-types/stub'
                        }
                    }
                );
                break;

            case 'bookingItem':
                const extraBookingItems = field.extra?.allowedValues === 'selected' ? field.extra?.bookingItems : undefined;
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/booking-items/stub',
                            categoriesSourceExtraData: { ids: extraBookingItems }
                        }
                    }
                );
                break;

            case 'bookingOption':
                const extraBookingItemsForOptions = field.extra?.allowedValues === 'selected' ? field.extra?.bookingItems : undefined;
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/booking-items/options/stub',
                            categoriesSourceExtraData: { bookingItem: extraBookingItemsForOptions }
                        }
                    }
                );
                break;

            case 'venue':
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categoriesSource: '/venues/stub'
                        }
                    }
                );
                break;

            case 'paymentState':
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categories: [
                                { name: 'pending', label: $localize`Pending` },
                                { name: 'completed', label: $localize`Completed` },
                                { name: 'cancelled', label: $localize`Cancelled` },
                            ]
                        }
                    }
                );
                break;

            case 'paymentType':
                fields.push({
                        name: field.id,
                        label: field.label,
                        type: 'discrete_multiple',
                        required: true,
                        initialValue: field.initialValue,
                        options: {
                            categories: [
                                { name: 'payment', label: $localize`Payment` },
                                { name: 'refund', label: $localize`Refund` },
                            ]
                        }
                    }
                );
                break;
        }
        return fields;
    }
}
